import React, { useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import Avatar from "../avatar/Avatar";
import type { PresenceState } from "../../app/presence/presenceSlice";

/**
 * 「+N」集約付きのアバタースタック（要約表示）。
 *
 * 役割の整理（UI ガイドの 3 形式 + 要約）:
 *  - A: AvatarStack（同フォルダ）… アイコン並列の選択 UI。上限なし・横スクロール。
 *  - C: UserListRow             … 一覧・操作向けの行表示。
 *  - 本コンポーネント           … 「誰がいるか」の要約。収まらない分は +N に畳む。
 *
 * 出自はプロジェクトバナー（`project-banner/components/UserList.jsx`）の
 * ローカル実装。プロジェクト管理表（/console/projects）のメンバーシップセル
 * と共用するためグローバル化した。
 *
 * 表示数の決定:
 *  - `maxVisible` 指定時: その数まで表示（テーブルセル等、幅が内容依存の文脈用）。
 *  - 未指定時: コンテナ幅を ResizeObserver で監視して自動計算（バナー用）。
 *
 * オンラインバッジは presence ストア（`state.presence.onlineUserIds`）を
 * 内部参照するため、呼び出し側での配線は不要。
 */
export interface CollapsedAvatarStackItem {
  /** React key。バナーは `_key`、管理表は user_id を渡す。 */
  key: string | number;
  /** オンライン判定に使う AuthUser id。未指定ならバッジ非表示。 */
  userId?: number | string | null;
  avatarUrl?: string | null;
  displayName?: string | null;
}

export interface CollapsedAvatarStackProps {
  items: CollapsedAvatarStackItem[];
  /** アバターサイズ（px）。既定 28（バナー準拠）。 */
  size?: number;
  /** 表示上限。未指定ならコンテナ幅から自動計算する。 */
  maxVisible?: number;
  /**
   * +N カウンターの分母。`items` が要約（全員分でない）場合に総数を渡す。
   * 既定は `items.length`。
   */
  totalCount?: number;
  /** スタック全体のクリック（バナーの詳細パネル開閉等）。 */
  onClick?: () => void;
  /** 前面要素の重なり順を平坦化する（バナーの詳細パネル展開中に使用）。 */
  flat?: boolean;
  /** 横位置。バナーは "end"（右寄せ）、テーブルセルは "start"。既定 "end"。 */
  align?: "start" | "end";
  className?: string;
}

const AVATAR_OVERLAP = 2;
const COUNTER_ALLOWANCE = 20;

const StackContainer = styled.div<{ $align: "start" | "end"; $clickable: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === "end" ? "flex-end" : "flex-start"};
  position: relative;
  /* onClick なしでもボタン等の親のカーソルを妨げないよう inherit */
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "inherit")};
`;

const StackedAvatar = styled.div<{
  $index: number;
  $total: number;
  $flat: boolean;
}>`
  margin-left: -${AVATAR_OVERLAP}px;
  &:first-child {
    margin-left: 0;
  }
  position: relative;
  transition: all 0.2s ease;
  z-index: ${({ $flat, $total, $index }) => ($flat ? 1 : $total - $index)};

  &:hover {
    transform: translateY(-2px);
    z-index: ${({ $flat }) => ($flat ? 1 : 100)};
  }
`;

const Counter = styled.div.attrs({
  className: "u-row-center label8",
})`
  margin-left: -${AVATAR_OVERLAP}px;
  width: 16px;
  height: 16px;
  padding: 0 4px;
  background: #f0f0f0;
  border: 1px solid #fff;
  border-radius: 50%;
  color: #000 !important;
`;

const toNumericUserId = (
  userId: number | string | null | undefined,
): number | null => {
  if (userId === null || userId === undefined || userId === "") return null;
  const num = typeof userId === "number" ? userId : Number(userId);
  return Number.isFinite(num) ? num : null;
};

const CollapsedAvatarStack: React.FC<CollapsedAvatarStackProps> = ({
  items,
  size = 28,
  maxVisible,
  totalCount,
  onClick,
  flat = false,
  align = "end",
  className,
}) => {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const [measuredMax, setMeasuredMax] = useState(0);
  const isAutoWidth = maxVisible === undefined;
  const avatarStep = size - AVATAR_OVERLAP;

  const onlineUserIds = useSelector(
    (state: { presence?: PresenceState }) =>
      state.presence?.onlineUserIds ?? [],
  );
  const isOnline = (userId: CollapsedAvatarStackItem["userId"]): boolean => {
    const numericId = toNumericUserId(userId);
    return numericId !== null && onlineUserIds.includes(numericId);
  };

  // 幅自動計算モード: コンテナ幅を監視し、収まらない分を +N に集約する
  //（バナーのローカル実装を移植）。
  useLayoutEffect(() => {
    if (!isAutoWidth) return;
    const el = outerRef.current;
    if (!el || items.length === 0) return;

    const calculate = () => {
      const width = el.getBoundingClientRect().width;
      if (width <= 0) return;

      const allWidth = size + Math.max(0, items.length - 1) * avatarStep;
      if (allWidth <= width) {
        setMeasuredMax(items.length);
        return;
      }

      const budget = width - COUNTER_ALLOWANCE + AVATAR_OVERLAP;
      const count = Math.max(1, 1 + Math.floor((budget - size) / avatarStep));
      setMeasuredMax(Math.min(count, items.length));
    };

    const observer = new ResizeObserver(calculate);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAutoWidth, items.length, size, avatarStep]);

  if (items.length === 0) return null;

  const effectiveMax = isAutoWidth ? measuredMax : maxVisible;
  const visible = items.slice(0, effectiveMax);
  const remaining = Math.max(totalCount ?? items.length, items.length) - visible.length;

  return (
    <div
      ref={outerRef}
      className={className}
      style={isAutoWidth ? { width: "100%" } : undefined}
    >
      <StackContainer $align={align} $clickable={!!onClick} onClick={onClick}>
        {visible.map((item, index) => (
          <StackedAvatar
            key={String(item.key)}
            $index={index}
            $total={visible.length}
            $flat={flat}
          >
            <Avatar
              src={item.avatarUrl ?? undefined}
              size={size}
              name={item.displayName ?? undefined}
              tooltipText={item.displayName ?? undefined}
              tooltipPlacement="bottom"
              tooltipOffset={[0, 8]}
              isOnline={isOnline(item.userId)}
            />
          </StackedAvatar>
        ))}
        {remaining > 0 && <Counter>+{remaining}</Counter>}
      </StackContainer>
    </div>
  );
};

export default CollapsedAvatarStack;
